/**
 * ContactImportWizard — CSV import for the contact directory (ADR-141 §4
 * import lane).
 *
 * Four steps inside one dialog:
 *
 *   1. **Upload** — pick a CSV (template download available); the header
 *      row is parsed client-side for the mapping step.
 *   2. **Mapping** — map Name/Email/Phone/Tags to CSV columns (auto-
 *      guessed from common header spellings). Client-side pre-flight:
 *      Name plus at least one of Email/Phone; the server re-validates.
 *   3. **Preview** — server dry-run (`dry_run=true`, NO writes): summary
 *      counts + per-row create/update/error table.
 *   4. **Result** — commit enqueues the chunked task (202 + run id);
 *      poll until complete/failed, then show counts with a failed-rows
 *      CSV export. Contacts + tags query scopes invalidate on completion
 *      (the task writes while the poll is in flight).
 *
 * Precedents: `MappingCsvImport` (two-step dry-run→confirm dialog) and
 * `BulkInviteDialog` (template download + failed-rows export).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Center,
  Dialog,
  HStack,
  Portal,
  SimpleGrid,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button as ButtonComponent, SelectInput } from '@themes/aegix';
import { toaster } from '../../ui/toaster';
import { downloadBlob } from '../../../utils/deliveryMetricsExport';
import {
  IMPORT_FIELDS,
  IMPORT_FIELD_LABELS,
  downloadContactImportTemplate,
  guessColumnMapping,
  importErrorMessage,
  parseCsvHeader,
  runRowErrors,
  validateColumnMapping,
  type ContactImportDryRunResponse,
  type ContactImportRowReport,
  type ImportColumnMapping,
  type ImportField,
} from '../../../services/contacts.service';
import {
  isTerminalRunStatus,
  useContactImportCommit,
  useContactImportDryRun,
  useContactImportRun,
} from '../../../hooks/queries/useContactImport';
import {
  contactKeys,
  contactTagKeys,
} from '../../../hooks/queries/useContacts';

type WizardStep = 'upload' | 'mapping' | 'preview' | 'result';

interface ContactImportWizardProps {
  open: boolean;
  onClose: () => void;
}

const ACTION_LABELS: Record<ContactImportRowReport['action'], string> = {
  create: 'Create',
  update: 'Update',
  error: 'Error',
};

const ACTION_COLORS: Record<ContactImportRowReport['action'], string> = {
  create: 'green.700',
  update: 'blue.700',
  error: 'red.700',
};

/**
 * `File.text()` with a `FileReader` fallback — jsdom's File does not
 * implement the Blob read methods, so tests (and any older WebView)
 * take the reader path.
 */
function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsText(file);
  });
}

export function ContactImportWizard({
  open,
  onClose,
}: ContactImportWizardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ImportColumnMapping>({});
  const [showMappingError, setShowMappingError] = useState(false);
  const [preview, setPreview] = useState<ContactImportDryRunResponse | null>(
    null,
  );
  const [runId, setRunId] = useState<number | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);

  const dryRun = useContactImportDryRun();
  const commit = useContactImportCommit();
  const runQuery = useContactImportRun(runId);
  const run = runQuery.data ?? null;

  // The commit task creates contacts/tags while we poll — refresh both
  // scopes once the run lands (regardless of outcome; partial imports
  // still wrote rows).
  const runStatus = run?.status;
  useEffect(() => {
    if (isTerminalRunStatus(runStatus)) {
      queryClient.invalidateQueries({ queryKey: contactKeys.all });
      queryClient.invalidateQueries({ queryKey: contactTagKeys.all });
    }
  }, [runStatus, queryClient]);

  const mappingError = useMemo(() => validateColumnMapping(mapping), [mapping]);

  const resetAll = () => {
    setStep('upload');
    setFile(null);
    setHeaders([]);
    setMapping({});
    setShowMappingError(false);
    setPreview(null);
    setRunId(null);
    setTopLevelError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selected = event.target.files?.[0];
    if (!selected) {
      return;
    }
    setTopLevelError(null);
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setTopLevelError(t('contacts.import.chooseCsvFileError', 'Please choose a .csv file.'));
      setFile(null);
      setHeaders([]);
      return;
    }
    try {
      const text = await readFileText(selected);
      const parsed = parseCsvHeader(text);
      if (parsed.length === 0) {
        setTopLevelError(t('contacts.import.noHeaderRowError', 'The file has no header row.'));
        setFile(null);
        setHeaders([]);
        return;
      }
      setFile(selected);
      setHeaders(parsed);
      setMapping(guessColumnMapping(parsed));
      setShowMappingError(false);
      setPreview(null);
    } catch (err) {
      setTopLevelError((err as Error).message || t('contacts.import.couldNotReadFileError', 'Could not read the file.'));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const { blob, filename } = await downloadContactImportTemplate();
      downloadBlob(blob, filename, 'text/csv');
    } catch {
      toaster.create({ title: t('contacts.import.templateDownloadFailed', 'Template download failed'), type: 'error' });
    }
  };

  const handlePreview = async () => {
    setShowMappingError(true);
    if (mappingError !== null || file === null || dryRun.isPending) {
      return;
    }
    setTopLevelError(null);
    try {
      const report = await dryRun.mutateAsync({ file, mapping });
      setPreview(report);
      setStep('preview');
    } catch (err) {
      setTopLevelError(await importErrorMessage(err));
    }
  };

  const handleCommit = async () => {
    if (file === null || commit.isPending) {
      return;
    }
    setTopLevelError(null);
    try {
      const accepted = await commit.mutateAsync({ file, mapping });
      setRunId(accepted.id);
      setStep('result');
    } catch (err) {
      setTopLevelError(await importErrorMessage(err));
    }
  };

  const handleDownloadFailedRows = () => {
    if (!run) {
      return;
    }
    const failed = runRowErrors(run);
    if (failed.length === 0) {
      return;
    }
    const lines = [
      'Row,Errors',
      ...failed.map(
        (entry) =>
          `${entry.row},"${entry.errors.join('; ').replace(/"/g, '""')}"`,
      ),
    ];
    downloadBlob(`${lines.join('\r\n')}\r\n`, 'failed-rows.csv', 'text/csv');
  };

  const committableCount = preview
    ? preview.summary.create + preview.summary.update
    : 0;

  const headerOptions = headers.map((header) => ({
    label: header,
    value: header,
  }));

  const stepBody = () => {
    switch (step) {
      case 'upload':
        return (
          <VStack align="stretch" gap={4}>
            <Text fontSize="sm" color="text.secondary">
              {t(
                'contacts.import.uploadNotice',
                'Upload a UTF-8 CSV with a header row. You will map columns to contact fields in the next step — column names do not have to match the template.',
              )}
            </Text>
            <HStack gap={3}>
              <ButtonComponent
                type="button"
                visual="secondary"
                onClick={() => fileInputRef.current?.click()}
                data-e2e="importChooseFile"
              >
                {file
                  ? t('contacts.import.changeFile', 'Change file')
                  : t('contacts.import.chooseCsvFile', 'Choose CSV file…')}
              </ButtonComponent>
              <Text
                fontSize="sm"
                color={file ? 'text.primary' : 'text.secondary'}
                wordBreak="break-all"
              >
                {file?.name ?? t('contacts.import.noFileSelected', 'No file selected')}
              </Text>
            </HStack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              data-e2e="importFileInput"
              aria-label="Contact CSV file"
              onChange={(e) => void handleFileChange(e)}
            />
            <Box>
              <ButtonComponent
                type="button"
                visual="secondary"
                onClick={() => void handleDownloadTemplate()}
                data-e2e="importTemplateDownload"
              >
                {t('contacts.import.downloadTemplateCsv', 'Download template CSV')}
              </ButtonComponent>
            </Box>
          </VStack>
        );
      case 'mapping':
        return (
          <VStack align="stretch" gap={4} data-e2e="importMappingStep">
            <Text fontSize="sm" color="text.secondary">
              {t(
                'contacts.import.matchColumnsNotice',
                'Match contact fields to the columns found in {{filename}}. Name is required, plus at least one of Email or Phone. The Tags column may hold multiple tag names separated by semicolons.',
                { filename: file?.name },
              )}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              {IMPORT_FIELDS.map((field: ImportField) => (
                <SelectInput
                  key={field}
                  label={IMPORT_FIELD_LABELS[field]}
                  id={`import-mapping-${field}`}
                  e2eTitle={`importMapping-${field}`}
                  searchable={false}
                  option={headerOptions}
                  value={mapping[field] ?? ''}
                  placeholder={t('contacts.import.notImported', 'Not imported')}
                  onChange={(value: string) =>
                    setMapping((prev) => ({
                      ...prev,
                      [field]: value || undefined,
                    }))
                  }
                />
              ))}
            </SimpleGrid>
            {showMappingError && mappingError && (
              <Alert.Root status="error" data-e2e="importMappingError">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>{mappingError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
          </VStack>
        );
      case 'preview':
        if (!preview) {
          return null;
        }
        return (
          <VStack align="stretch" gap={4} data-e2e="importPreview">
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
              <SummaryStat label={t('contacts.import.rowsLabel', 'Rows')} value={preview.summary.total} />
              <SummaryStat
                label={t('contacts.import.createAction', 'Create')}
                value={preview.summary.create}
                color="green.700"
              />
              <SummaryStat
                label={t('contacts.import.updateAction', 'Update')}
                value={preview.summary.update}
                color="blue.700"
              />
              <SummaryStat
                label={t('contacts.import.errorsLabel', 'Errors')}
                value={preview.summary.error}
                color={preview.summary.error > 0 ? 'red.700' : undefined}
              />
            </SimpleGrid>
            {preview.summary.tags_to_create.length > 0 && (
              <Text fontSize="sm" color="text.secondary">
                {t('contacts.import.newTagsToCreate', 'New tags to create:')}{' '}
                <Text as="span" color="text.primary">
                  {preview.summary.tags_to_create.join(', ')}
                </Text>
              </Text>
            )}
            <Box maxH="320px" overflowY="auto" borderWidth="1px" borderColor="border.tertiary" borderRadius="md">
              <Table.Root size="sm">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>{t('contacts.import.colLine', 'Line')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('contacts.import.colAction', 'Action')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('common.name', 'Name')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('common.email', 'Email')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('contacts.import.colPhone', 'Phone')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('contacts.import.colTags', 'Tags')}</Table.ColumnHeader>
                    <Table.ColumnHeader>{t('contacts.import.colErrors', 'Errors')}</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {preview.rows.map((row) => (
                    <Table.Row key={row.row} data-e2e="importPreviewRow">
                      <Table.Cell>{row.row}</Table.Cell>
                      <Table.Cell>
                        <Text color={ACTION_COLORS[row.action]}>
                          {row.action === 'create'
                            ? t('contacts.import.createAction', 'Create')
                            : row.action === 'update'
                            ? t('contacts.import.updateAction', 'Update')
                            : t('contacts.import.errorAction', 'Error')}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>{row.display_name || '—'}</Table.Cell>
                      <Table.Cell>{row.email || '—'}</Table.Cell>
                      <Table.Cell>{row.phone || '—'}</Table.Cell>
                      <Table.Cell>{row.tags.join(', ') || '—'}</Table.Cell>
                      <Table.Cell>
                        <Text color="red.700">{row.errors.join('; ')}</Text>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
            {committableCount === 0 && (
              <Alert.Root status="warning" data-e2e="importNothingToCommit">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Description>
                    {t(
                      'contacts.import.noImportableRows',
                      'No importable rows — fix the errors in the file and try again.',
                    )}
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}
          </VStack>
        );
      case 'result': {
        const finished = isTerminalRunStatus(run?.status);
        const failedRows = run ? runRowErrors(run) : [];
        return (
          <VStack align="stretch" gap={4} data-e2e="importResult">
            {!finished ? (
              <Center py={8}>
                <VStack gap={3}>
                  <Spinner size="lg" color="button.primary" />
                  <Text fontSize="sm" color="text.secondary">
                    {t(
                      'contacts.import.importingBackgroundNotice',
                      'Importing contacts… this runs in the background and may take a moment for large files.',
                    )}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <>
                {run?.status === 'failed' ? (
                  <Alert.Root status="error" data-e2e="importRunFailed">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>{t('contacts.import.importFailedTitle', 'Import failed')}</Alert.Title>
                      <Alert.Description>
                        {t(
                          'contacts.import.importFailedDesc',
                          'The import task did not complete. Partial rows may have been written — review the contact list and the failed-rows report.',
                        )}
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : (
                  <Alert.Root status="success" data-e2e="importRunComplete">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>{t('contacts.import.importCompleteTitle', 'Import complete')}</Alert.Title>
                    </Alert.Content>
                  </Alert.Root>
                )}
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                  <SummaryStat label={t('contacts.import.rowsLabel', 'Rows')} value={run?.total_rows ?? 0} />
                  <SummaryStat
                    label={t('contacts.import.createdLabel', 'Created')}
                    value={run?.created_count ?? 0}
                    color="green.700"
                  />
                  <SummaryStat
                    label={t('contacts.import.updatedLabel', 'Updated')}
                    value={run?.updated_count ?? 0}
                    color="blue.700"
                  />
                  <SummaryStat
                    label={t('contacts.import.errorsLabel', 'Errors')}
                    value={run?.error_count ?? 0}
                    color={(run?.error_count ?? 0) > 0 ? 'red.700' : undefined}
                  />
                </SimpleGrid>
                {failedRows.length > 0 && (
                  <Box>
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      onClick={handleDownloadFailedRows}
                      data-e2e="importFailedRowsExport"
                    >
                      {t('contacts.import.downloadFailedRowsCsv', 'Download failed rows CSV')}
                    </ButtonComponent>
                  </Box>
                )}
              </>
            )}
          </VStack>
        );
      }
    }
  };

  const stepFooter = () => {
    switch (step) {
      case 'upload':
        return (
          <>
            <ButtonComponent visual="secondary" onClick={handleClose}>
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              disabled={file === null}
              onClick={() => setStep('mapping')}
              data-e2e="importUploadNext"
            >
              {t('common.next', 'Next')}
            </ButtonComponent>
          </>
        );
      case 'mapping':
        return (
          <>
            <ButtonComponent
              visual="secondary"
              onClick={() => setStep('upload')}
            >
              {t('common.back', 'Back')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              disabled={dryRun.isPending}
              onClick={() => void handlePreview()}
              data-e2e="importMappingPreview"
            >
              {dryRun.isPending ? t('contacts.import.validating', 'Validating…') : t('contacts.import.preview', 'Preview')}
            </ButtonComponent>
          </>
        );
      case 'preview':
        return (
          <>
            <ButtonComponent
              visual="secondary"
              onClick={() => setStep('mapping')}
            >
              {t('common.back', 'Back')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              disabled={commit.isPending || committableCount === 0}
              onClick={() => void handleCommit()}
              data-e2e="importCommit"
            >
              {commit.isPending
                ? t('contacts.import.importing', 'Importing…')
                : t('contacts.import.importContactsCount', 'Import {{count}} contact{{plural}}', {
                    count: committableCount,
                    plural: committableCount === 1 ? '' : 's',
                  })}
            </ButtonComponent>
          </>
        );
      case 'result':
        return (
          <ButtonComponent
            visual="primary"
            onClick={handleClose}
            data-e2e="importDone"
          >
            {isTerminalRunStatus(run?.status) ? t('common.done', 'Done') : t('common.close', 'Close')}
          </ButtonComponent>
        );
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxW="900px" data-e2e="importWizard">
            <Dialog.Header>
              <Dialog.Title>{t('contacts.import.dialogTitle', 'Import contacts from CSV')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                {topLevelError && (
                  <Alert.Root status="error" data-e2e="importError">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Description>{topLevelError}</Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                )}
                {stepBody()}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>{stepFooter()}</HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

function SummaryStat({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color?: string;
}) {
  return (
    <Box
      borderWidth="1px"
      borderColor="border.tertiary"
      borderRadius="md"
      p={3}
    >
      <Text fontSize="xs" color="text.secondary">
        {label}
      </Text>
      <Text
        fontSize="lg"
        fontWeight={600}
        color={color ?? 'text.primary'}
        data-e2e={`importStat-${label.toLowerCase()}`}
      >
        {value}
      </Text>
    </Box>
  );
}
